import { PublishedBenefitsResponse } from '@/api/benefit.api';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { LabelValue } from '@/types/global';
import { getBenefitNameFromType } from '@/utils/benefits/getBenefitTypeName';
import { parseCircuitBenefit } from '@/utils/circuits/parseCircuitBenefit';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import {
  getRepresentativePensionPrice,
  resolvePensionPricings,
} from '@/utils/estimates/hotel/computeHotelPensionPrice';

export type BenefitFiltersType = {
  search: string;
  hotelCity?: LabelValue<string>;
  hotelClassifications?: Array<LabelValue<string>>;
  transportType: 'transfer' | 'car';
  filters: Array<'VOL' | 'VEHICLE' | 'VEHICLE_WITH_CHAUFFEUR' | 'TRANSFER'>;
  standardBenefit: BenefitFormData;
  optionalBenefits: Array<BenefitFormData>;
  standardBenefitPensionType?: HotelPensionType;
  optionalBenefitsPensionTypes?: Record<string, HotelPensionType>;
};

interface BenefitSelectionTableProps {
  control: Control<BenefitFiltersType>;
  publishedBenefits: BenefitFormData[];
  title: string;
  benefitType?: BenefitType;
  isViewMode?: boolean;
}

const hotelPensionColumns: { code: string; value: HotelPensionType }[] = [
  { code: 'RO', value: 'SingleRoom' },
  { code: 'BB', value: 'Breakfast' },
  { code: 'HB', value: 'SemiPension' },
  { code: 'FB', value: 'FullPension' },
  { code: 'AI', value: 'AllInclusive' },
];

const hasPositivePensionPrice = (price: {
  adultPrice?: number | null;
  childPrice?: number | null;
  adolescentPrice?: number | null;
}) =>
  (price.adultPrice ?? 0) > 0 ||
  (price.childPrice ?? 0) > 0 ||
  (price.adolescentPrice ?? 0) > 0;

const getAvailablePensions = (benefit: BenefitFormData) => {
  const set = new Set<HotelPensionType>();
  const basePensionType = benefit.hotel?.pricingPensionType as
    | HotelPensionType
    | undefined;

  if (basePensionType) set.add(basePensionType);

  const pensionPricings = resolvePensionPricings(
    benefit.hotel?.commonPensionPricings ?? [],
    benefit.hotel?.HotelRoomCategory?.flatMap(
      (category) => category.categoryPensionPricings ?? [],
    ) ?? [],
    benefit.hotel?.areOtherPensionPricesCommon ?? false,
  );

  pensionPricings.forEach((pricing) => {
    if (!pricing.periods?.length) return;
    pricing.prices?.forEach((price) => {
      if (hasPositivePensionPrice(price)) set.add(price.pensionType);
    });
  });

  return set;
};

const getDefaultPensionType = (
  benefit: BenefitFormData,
  availablePensions: Set<HotelPensionType>,
) => {
  const availableColumns = hotelPensionColumns.filter((column) =>
    availablePensions.has(column.value),
  );
  if (availableColumns.length === 0) return undefined;
  if (availableColumns.length === 1) return availableColumns[0]?.value;

  const basePensionType = benefit.hotel?.pricingPensionType as
    | HotelPensionType
    | undefined;
  const pensionPricings = resolvePensionPricings(
    benefit.hotel?.commonPensionPricings ?? [],
    benefit.hotel?.HotelRoomCategory?.flatMap(
      (category) => category.categoryPensionPricings ?? [],
    ) ?? [],
    benefit.hotel?.areOtherPensionPricesCommon ?? false,
  );

  const sorted = availableColumns
    .map((column) => {
      if (basePensionType && column.value === basePensionType) {
        return {
          pensionType: column.value,
          score: 0,
          order: hotelPensionColumns.findIndex((c) => c.value === column.value),
        };
      }

      const representative = getRepresentativePensionPrice(
        pensionPricings,
        column.value,
      );
      const score =
        (representative?.adultPrice ?? 0) +
        (representative?.childPrice ?? representative?.adultPrice ?? 0) +
        (representative?.adolescentPrice ?? representative?.adultPrice ?? 0);

      return {
        pensionType: column.value,
        score,
        order: hotelPensionColumns.findIndex((c) => c.value === column.value),
      };
    })
    .toSorted((a, b) =>
      a.score === b.score ? a.order - b.order : a.score - b.score,
    );

  return sorted[0]?.pensionType;
};

const CircuitModalBenefitSelectionTable = ({
  control,
  publishedBenefits,
  benefitType,
  title,
  isViewMode = false,
}: BenefitSelectionTableProps) => {
  const { t } = useTranslation();
  const benefits = useMemo(
    () =>
      (benefitType
        ? publishedBenefits.filter(
            (benefit) => benefit.type === getBenefitNameFromType(benefitType),
          )
        : publishedBenefits
      ).map((benefit) => parseCircuitBenefit(benefit)),
    [benefitType, publishedBenefits],
  );
  const isHotelTable = benefitType === BenefitType.HOTEL;
  const availablePensionsByBenefit = useMemo(
    () =>
      new Map(
        benefits.map((benefit) => [benefit.id, getAvailablePensions(benefit)]),
      ),
    [benefits],
  );
  const visibleHotelPensionColumns = useMemo(() => {
    if (!isHotelTable) return [];
    const used = new Set<HotelPensionType>();
    availablePensionsByBenefit.forEach((pensions) => {
      pensions.forEach((pension) => used.add(pension));
    });
    return hotelPensionColumns.filter((column) => used.has(column.value));
  }, [availablePensionsByBenefit, isHotelTable]);

  return (
    benefits.length > 0 && (
      <table className="w-full rounded-md table-auto">
        <colgroup>
          <col style={{ width: isHotelTable ? '34%' : '76%' }} />
          {isHotelTable &&
            visibleHotelPensionColumns.map((column) => (
              <col
                key={`col-standard-${column.value}`}
                style={{ width: '6%' }}
              />
            ))}
          {isHotelTable &&
            visibleHotelPensionColumns.map((column) => (
              <col key={`col-option-${column.value}`} style={{ width: '6%' }} />
            ))}
          {!isHotelTable && (
            <>
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
            </>
          )}
        </colgroup>
        <thead>
          {isHotelTable ? (
            <>
              <tr>
                <th className="text-left pr-2" rowSpan={2}>
                  <Label className="font-bold text-gray-700">{title}</Label>
                </th>
                <th
                  className="text-center px-2"
                  colSpan={Math.max(visibleHotelPensionColumns.length, 1)}
                >
                  <Label>{t('circuits.editorial.tables.standard')}</Label>
                </th>
                <th
                  className="text-center px-2"
                  colSpan={Math.max(visibleHotelPensionColumns.length, 1)}
                >
                  <Label>{t('circuits.editorial.tables.option')}</Label>
                </th>
              </tr>
              <tr>
                {visibleHotelPensionColumns.map((column) => (
                  <th
                    key={`standard-${column.value}`}
                    className="text-center px-1"
                  >
                    <Label>{column.code}</Label>
                  </th>
                ))}
                {visibleHotelPensionColumns.map((column) => (
                  <th
                    key={`option-${column.value}`}
                    className="text-center px-1"
                  >
                    <Label>{column.code}</Label>
                  </th>
                ))}
              </tr>
            </>
          ) : (
            <tr>
              <th className="text-left pr-2">
                <Label className="font-bold text-gray-700">{title}</Label>
              </th>
              <th className="text-center pr-1">
                <Label>{t('circuits.editorial.tables.standard')}</Label>
              </th>
              <th className="text-center pl-1">
                <Label>{t('circuits.editorial.tables.option')}</Label>
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {benefits.map((benefit) => {
            const availablePensions =
              availablePensionsByBenefit.get(benefit.id) ??
              new Set<HotelPensionType>();
            const defaultPensionType = getDefaultPensionType(
              benefit,
              availablePensions,
            );

            return (
              <tr key={benefit.id}>
                <td className="text-left pr-2 wrap-break-word">
                  {benefit.name}
                </td>
                {isHotelTable ? (
                  visibleHotelPensionColumns.map((column) => (
                    <td
                      key={`standard-cell-${benefit.id}-${column.value}`}
                      className="text-center px-1"
                    >
                      <div className="flex justify-center">
                        <AccommodationStandardPensionCheckbox
                          control={control}
                          benefit={benefit}
                          pensionType={column.value}
                          availablePensions={availablePensions}
                          defaultPensionType={defaultPensionType}
                          isViewMode={isViewMode}
                        />
                      </div>
                    </td>
                  ))
                ) : (
                  <>
                    <td className="text-center pr-1">
                      <div className="flex justify-center">
                        <StandardBenefitCheckbox
                          control={control}
                          benefit={benefit}
                          isAccommodationTable={isHotelTable}
                          isViewMode={isViewMode}
                        />
                      </div>
                    </td>
                    <td className="text-center pl-1">
                      <div className="flex justify-center">
                        <OptionalBenefitCheckbox
                          control={control}
                          benefit={benefit}
                          isAccommodationTable={isHotelTable}
                          isViewMode={isViewMode}
                        />
                      </div>
                    </td>
                  </>
                )}
                {isHotelTable &&
                  visibleHotelPensionColumns.map((column) => (
                    <td
                      key={`option-cell-${benefit.id}-${column.value}`}
                      className="text-center px-1"
                    >
                      <div className="flex justify-center">
                        <AccommodationOptionalPensionCheckbox
                          control={control}
                          benefit={benefit}
                          pensionType={column.value}
                          availablePensions={availablePensions}
                          defaultPensionType={defaultPensionType}
                          isViewMode={isViewMode}
                        />
                      </div>
                    </td>
                  ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    )
  );
};

interface BenefitCheckboxProps {
  control: Control<BenefitFiltersType>;
  benefit: BenefitFormData;
  isAccommodationTable?: boolean;
  isViewMode?: boolean;
}

interface AccommodationPensionCheckboxProps extends BenefitCheckboxProps {
  pensionType: HotelPensionType;
  availablePensions: Set<HotelPensionType>;
  defaultPensionType?: HotelPensionType;
}

const OptionalBenefitCheckbox = ({
  control,
  benefit,
  isAccommodationTable = false,
  isViewMode = false,
}: BenefitCheckboxProps) => {
  return (
    <Controller
      control={control}
      name="optionalBenefits"
      render={({ field }) => (
        <Controller
          control={control}
          name="standardBenefit"
          render={({ field: standardField }) => {
            field.value = field.value ?? [];
            const hasBenefit = field.value
              .map((value) => value.id)
              .includes(benefit.id);
            return (
              <Checkbox
                checked={hasBenefit}
                onClick={() =>
                  field.onChange(
                    hasBenefit
                      ? field.value.filter((value) => value.id !== benefit.id)
                      : [...field.value, benefit],
                  )
                }
                value={benefit.id}
                disabled={
                  isViewMode ||
                  (isAccommodationTable &&
                    standardField.value?.id === benefit.id)
                }
              />
            );
          }}
        />
      )}
    />
  );
};

const StandardBenefitCheckbox = ({
  control,
  benefit,
  isAccommodationTable = false,
  isViewMode = false,
}: BenefitCheckboxProps) => {
  return (
    <Controller
      control={control}
      name="standardBenefit"
      render={({ field }) => (
        <Controller
          control={control}
          name="optionalBenefits"
          render={({ field: optionalField }) => {
            field.value = field.value ?? [];
            const isOptionalSelected = (optionalField.value ?? [])
              .map((value) => value.id)
              .includes(benefit.id);
            return (
              <Checkbox
                checked={field.value.id === benefit.id}
                onClick={() =>
                  field.onChange(
                    field.value.id === benefit.id ? undefined : benefit,
                  )
                }
                value={benefit.id}
                disabled={
                  isViewMode || (isAccommodationTable && isOptionalSelected)
                }
              />
            );
          }}
        />
      )}
    />
  );
};

const AccommodationStandardPensionCheckbox = ({
  control,
  benefit,
  pensionType,
  availablePensions,
  defaultPensionType,
  isViewMode = false,
}: AccommodationPensionCheckboxProps) => {
  return (
    <Controller
      control={control}
      name="standardBenefit"
      render={({ field: standardField }) => (
        <Controller
          control={control}
          name="optionalBenefits"
          render={({ field: optionalField }) => (
            <Controller
              control={control}
              name="standardBenefitPensionType"
              render={({ field: standardPensionField }) => {
                const isAvailable = availablePensions.has(pensionType);
                if (!isAvailable) return <></>;

                const isChecked =
                  standardField.value?.id === benefit.id &&
                  standardPensionField.value === pensionType;

                return (
                  <Checkbox
                    checked={isChecked}
                    disabled={isViewMode}
                    onClick={() => {
                      const optionalsWithoutHotel = (
                        optionalField.value ?? []
                      ).filter((value) => value.id !== benefit.id);
                      if (isChecked) {
                        standardField.onChange(undefined);
                        standardPensionField.onChange(undefined);
                        optionalField.onChange(optionalsWithoutHotel);
                        return;
                      }

                      standardField.onChange(benefit);
                      standardPensionField.onChange(
                        pensionType ?? defaultPensionType,
                      );
                      optionalField.onChange(optionalsWithoutHotel);
                    }}
                  />
                );
              }}
            />
          )}
        />
      )}
    />
  );
};

const AccommodationOptionalPensionCheckbox = ({
  control,
  benefit,
  pensionType,
  availablePensions,
  defaultPensionType,
  isViewMode = false,
}: AccommodationPensionCheckboxProps) => {
  return (
    <Controller
      control={control}
      name="standardBenefit"
      render={({ field: standardField }) => (
        <Controller
          control={control}
          name="optionalBenefits"
          render={({ field: optionalField }) => (
            <Controller
              control={control}
              name="optionalBenefitsPensionTypes"
              render={({ field: optionalPensionsField }) => {
                const isAvailable = availablePensions.has(pensionType);
                if (!isAvailable) return <></>;

                const isOptionalHotel = (optionalField.value ?? [])
                  .map((value) => value.id)
                  .includes(benefit.id);
                const isChecked =
                  isOptionalHotel &&
                  optionalPensionsField.value?.[benefit.id] === pensionType;

                return (
                  <Checkbox
                    checked={isChecked}
                    disabled={
                      isViewMode || standardField.value?.id === benefit.id
                    }
                    onClick={() => {
                      const nextOptionalPensions = {
                        ...optionalPensionsField.value,
                      };

                      if (isChecked) {
                        optionalField.onChange(
                          (optionalField.value ?? []).filter(
                            (value) => value.id !== benefit.id,
                          ),
                        );
                        delete nextOptionalPensions[benefit.id];
                        optionalPensionsField.onChange(nextOptionalPensions);
                        return;
                      }

                      if (!isOptionalHotel) {
                        optionalField.onChange([
                          ...(optionalField.value ?? []),
                          benefit,
                        ]);
                      }

                      nextOptionalPensions[benefit.id] =
                        pensionType ?? defaultPensionType ?? pensionType;
                      optionalPensionsField.onChange(nextOptionalPensions);
                    }}
                  />
                );
              }}
            />
          )}
        />
      )}
    />
  );
};

type CircuitModalBenefitSelectionTablesByCityProps = Omit<
  BenefitSelectionTableProps,
  'publishedBenefits'
> & {
  publishedBenefits: PublishedBenefitsResponse;
};

const CircuitModalBenefitSelectionTablesByCity = ({
  control,
  publishedBenefits,
  title,
  benefitType,
  isViewMode,
}: CircuitModalBenefitSelectionTablesByCityProps) => {
  return (
    <>
      <h2 className="text-base font-medium text-black">{title}</h2>
      {publishedBenefits &&
        Object.keys(publishedBenefits)
          .sort()
          .filter((city) => city !== 'null')
          .map((city, indexCity) => (
            <CircuitModalBenefitSelectionTable
              key={`${city}-${indexCity}`}
              control={control}
              publishedBenefits={publishedBenefits[city] ?? []}
              benefitType={benefitType}
              title={city}
              isViewMode={isViewMode}
            />
          ))}
      {publishedBenefits &&
        publishedBenefits['null'] &&
        publishedBenefits['null'].length > 0 && (
          <CircuitModalBenefitSelectionTable
            control={control}
            publishedBenefits={publishedBenefits['null'] ?? []}
            benefitType={benefitType}
            title=""
            isViewMode={isViewMode}
          />
        )}
    </>
  );
};

export default CircuitModalBenefitSelectionTablesByCity;
